import { useEffect, useRef, useState } from "react";
import { useLocale, useCurrency } from "../../intl";
import { localeToUrlSegment } from "../../intl/manifestI18n";
import { getI18nRollout } from "../../intl/i18nRollout.js";
import { AUTOMAP_DEALER_PORTAL_I18N_MANIFEST } from "../../constants/automapDealerPortalI18n";
import { FX_CURRENCY_ANALYTICS_SURFACES, FX_CURRENCY_SOURCE_COMPONENTS } from "../../constants/fxCurrencyAnalyticsConstants.js";

const LOCALE_LABELS = {
  "es-CO": "ES",
  "en-US": "EN",
};

function LocalePillSelect({ id, label, value, options, onChange, onOpenChange, isOpen }) {
  const selected = options.find((option) => option.value === value);

  return (
    <div className={`automap-locale-pill${isOpen ? " is-open" : ""}`}>
      <button
        type="button"
        id={id}
        className="automap-locale-pill__trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={label}
        onClick={() => onOpenChange(!isOpen)}
      >
        <span className="automap-locale-pill__label">{label}</span>
        <span className="automap-locale-pill__value">{selected?.label || value}</span>
        <span className="automap-locale-pill__chevron" aria-hidden="true">
          ▾
        </span>
      </button>
      {isOpen ? (
        <ul className="automap-locale-pill__menu" role="listbox" aria-labelledby={id}>
          {options.map((option) => (
            <li key={option.value}>
              <button
                type="button"
                role="option"
                aria-selected={option.value === value}
                className={option.value === value ? "is-selected" : undefined}
                onClick={() => {
                  onChange(option.value);
                  onOpenChange(false);
                }}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export default function DealerPortalLocaleControls() {
  const { locale, setLocale, supportedLocales, t } = useLocale();
  const { displayCurrency, setDisplayCurrency, supportedCurrencies } = useCurrency();
  const manifestI18n = AUTOMAP_DEALER_PORTAL_I18N_MANIFEST.i18n;
  const rollout = getI18nRollout({ i18n: manifestI18n });
  const containerRef = useRef(null);
  const [openMenu, setOpenMenu] = useState(null);

  const localeChoices =
    supportedLocales.length > 1 ? supportedLocales : manifestI18n.supportedLocales;

  const localeOptions =
    localeChoices.length > 1 && rollout.clientViewI18nEnabled !== false
      ? localeChoices.map((value) => ({
          value,
          label: LOCALE_LABELS[value] || localeToUrlSegment(value)?.toUpperCase() || value,
        }))
      : [];

  const currencyChoices =
    supportedCurrencies.length > 1 ? supportedCurrencies : manifestI18n.supportedCurrencies;

  const currencyOptions =
    currencyChoices.length > 1 && rollout.displayCurrencyEnabled !== false
      ? currencyChoices.map((option) => ({ value: option, label: option }))
      : [];

  const showLanguage = localeOptions.length >= 2;
  const showCurrency = currencyOptions.length >= 2;

  useEffect(() => {
    if (!openMenu) return undefined;

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setOpenMenu(null);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpenMenu(null);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [openMenu]);

  if (!showLanguage && !showCurrency) {
    return null;
  }

  return (
    <div ref={containerRef} className="dealer-portal-locale-controls automap-locale-controls">
      {showLanguage ? (
        <LocalePillSelect
          id="dealer-portal-language-select"
          label={t("common.language")}
          value={locale}
          options={localeOptions}
          onChange={setLocale}
          isOpen={openMenu === "language"}
          onOpenChange={(nextOpen) => setOpenMenu(nextOpen ? "language" : null)}
        />
      ) : null}
      {showCurrency ? (
        <LocalePillSelect
          id="dealer-portal-currency-select"
          label={t("common.currency")}
          value={displayCurrency}
          options={currencyOptions}
          onChange={(nextCurrency) =>
            setDisplayCurrency(nextCurrency, {
              sourceComponent: FX_CURRENCY_SOURCE_COMPONENTS.DEALER_PORTAL_LOCALE_CONTROLS,
              surface: FX_CURRENCY_ANALYTICS_SURFACES.CURRENCY_CHANGE,
            })
          }
          isOpen={openMenu === "currency"}
          onOpenChange={(nextOpen) => setOpenMenu(nextOpen ? "currency" : null)}
        />
      ) : null}
    </div>
  );
}
